import { Fragment, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import styles from './InviteYourTeam.module.scss'

import Button from '@/components/Button'
import { useAnalytics } from '@/hooks/useAnalytics'
import Input from '@/pages/app/GetStarted/components/Input/Input'
import { selectEmail } from '@/store/features/user/userSlice'
import { EventName } from '@/utils/eventDefinitions'

// Define the form schema with email validation. currentUserEmail defaults to '' pre-load
// (selectEmail), which never matches a real email, so the self-check fails open until
// the profile fetch resolves rather than false-blocking.
const buildInviteTeamSchema = (currentUserEmail: string) => {
  const normalizedSelf = currentUserEmail.trim().toLowerCase()
  return z.object({
    emails: z.array(
      z
        .string()
        .email()
        .or(z.literal(''))
        .refine((email) => !normalizedSelf || email.trim().toLowerCase() !== normalizedSelf, {
          message: 'You cannot invite yourself',
        })
    ).min(1),
  })
}

type InviteTeamFormData = z.infer<ReturnType<typeof buildInviteTeamSchema>>

/** The form below can render as many email fields as you want, this is your knob. */
const NUM_EMAIL_FIELDS = 3

interface InviteYourTeamProps {
  /** Pass emails you want to be pre-filled in the form (up to NUM_EMAIL_FIELDS) */
  initialEmails?: string[]
  /** Callback invoked when the form is submitted with at least one valid email */
  onSubmit?: (emails: string[]) => void
}

export default function InviteYourTeam(props: InviteYourTeamProps) {
  const { trackEvent } = useAnalytics()
  const currentUserEmail = useSelector(selectEmail)
  const inviteTeamSchema = useMemo(() => buildInviteTeamSchema(currentUserEmail), [currentUserEmail])
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<InviteTeamFormData>({
    resolver: zodResolver(inviteTeamSchema),
    defaultValues: {
      emails: props.initialEmails?.length || 0 > 0 ? props.initialEmails : Array(NUM_EMAIL_FIELDS).fill(''),
    },
    mode: 'onBlur',
  })

  const onSubmit = (data: InviteTeamFormData) => {
    // Filter out empty emails before submitting
    const validEmails = data.emails.filter((email) => email.trim() !== '')
    // TODO: Should we deduplicate the emails?
    trackEvent?.(EventName.OnboardingTeamInvited, { count: validEmails.length })
    props.onSubmit?.(validEmails)
  }

  return (
    <div className={styles.root}>
      <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
        {Array.from({ length: NUM_EMAIL_FIELDS }).map((_, index) => (
          <Fragment key={index}>
            <Input
              {...register(`emails.${index}`)}
              type="email"
              className={styles.input}
              placeholder="email@example.com"
            />
            {errors.emails?.[index] && <span className={styles.error}>{errors.emails[index].message}</span>}
          </Fragment>
        ))}

        <div className={styles.note}>You can also add team members after team creation</div>

        <Button title="Continue" type="dark" onClick={handleSubmit(onSubmit)} />
      </form>
    </div>
  )
}
